import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts"
import { useTranslation } from "react-i18next"
import { type UsageChartProps } from "../../../types/analytics"

export const UsageChart: React.FC<UsageChartProps> = ({
  data,
  threshold,
  currentUsage,
  subscriptionName: _subscriptionName,
  startOfCycle,
  endOfCycle,
  showOverlay = false,
}) => {
  const { t } = useTranslation()

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-card p-3 rounded-lg border">
          <div className="font-semibold text-foreground">{label}</div>
          {data.usage !== null && (
            <div className="text-sm text-muted-foreground">
              {t("analytics.usageChart.actual")}: {data.usage.toLocaleString()}
            </div>
          )}
        </div>
      )
    }
    return null
  }

  if (showOverlay)
    return (
      <div className="p-4 bg-card rounded border min-h-[400px]">
        <h3 className="mb-3 text-lg font-semibold tracking-tight text-foreground">
          Usage Tracking - Loading...
        </h3>
        <div className="h-[300px] bg-muted rounded" />
      </div>
    )

  return (
    <div className="p-6 bg-card rounded-lg border h-full">
      <div className="flex flex-col gap-4 h-full">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            {t("analytics.usageChart.title")}
          </h3>
          <p className="text-sm text-muted-foreground">{t("analytics.usageChart.subtitle")}</p>
        </div>
        <div className="flex gap-4 flex-1">
          <div className="flex-1 min-h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <Tooltip content={<CustomTooltip />} />

                <ReferenceLine
                  y={threshold}
                  stroke="#ef4444"
                  strokeDasharray="5 5"
                  label={{ value: t("analytics.usageChart.limit"), position: "top" }}
                />
                <Line
                  type="monotone"
                  dataKey="usage"
                  stroke="#10b981"
                  strokeWidth={3}
                  dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }}
                  connectNulls={false}
                  name={t("analytics.usageChart.actual")}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="w-80">
            <div className="h-full p-4 bg-muted/50 rounded-lg border">
              <h4 className="mb-3 text-base font-semibold tracking-tight text-foreground">
                {t("analytics.quickStats.title")}
              </h4>
              <div className="flex flex-col gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("analytics.quickStats.currentUsage")}
                  </p>
                  <p className="text-2xl font-semibold tracking-tight text-foreground">
                    {currentUsage?.toLocaleString() || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("analytics.quickStats.last30Days")}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("analytics.quickStats.period")}
                  </p>
                  <p className="text-sm font-medium text-foreground">
                    {startOfCycle} - {endOfCycle}
                  </p>
                </div>
                <div className="border-t border-border/70 pt-4">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t("analytics.usageChart.title")}
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="h-[3px] w-3 rounded-sm bg-green-500" />
                      <span className="text-sm font-medium text-foreground">
                        {t("analytics.usageChart.actual")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-[3px] w-3 rounded-sm bg-red-500"
                        style={{
                          backgroundImage:
                            "repeating-linear-gradient(to right, #ef4444 0, #ef4444 3px, transparent 3px, transparent 6px)",
                        }}
                      />
                      <span className="text-sm font-medium text-foreground">
                        {t("analytics.usageChart.limit")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
